import React, { memo, useMemo } from 'react';
import { Progress } from "@/components/ui/progress";
import { Typewriter } from "@/components/ui/typewriter";
import { motion } from "framer-motion";

interface LoadingPageProps {
  progress: number;
}

const shuffleArray = <T,>(array: T[]): T[] => {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
};

const LoadingText = memo(() => {
  const texts = useMemo(() => shuffleArray([
    "Crafting your perfect puzzle...",
    "Finding the right words...",
    "Arranging the grid...",
    "Weaving the word web...",
    "Applying the finishing ink…",
    "Putting the final piece in place…",
    "Crossing the T's and dotting the I's…",
    "Sharpening digital pencils…",
    "Tuning the clue-generator…",
    "Wrestling with word placement...",
    "Knitting a grid of curiosity..."
  ]), []);

  return (
    <h1 className="text-3xl font-bold text-primary mb-2 w-full text-center">
      <Typewriter 
        text={texts}
        speed={70}
        deleteSpeed={50}
        waitTime={2000}
        loop={true}
      />
    </h1>
  );
});

LoadingText.displayName = 'LoadingText';

const LoadingPage: React.FC<LoadingPageProps> = ({ progress }) => {
  return (
    <div className="flex flex-col items-center justify-center bg-gradient-to-b from-background to-background/80 p-4 h-[300px]">
      <motion.div 
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-xl mx-auto flex flex-col items-center gap-8"
      >
        <div className="h-[40px] flex items-center">
          <LoadingText />
        </div>
        
        <div className="w-full space-y-4">
          <Progress value={progress} className="h-2" />
          <p className="text-green-800 text-center">
            This may take a few moments while we create your personalized crossword puzzle
          </p>
        </div>
      </motion.div>
    </div>
  );
};

export default LoadingPage; 